/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 26/12/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 09/09/2026 | Lic. José Luis Aragón Cervantes  | Aplicar descuento: tamaño modal acorde a arquitectura, sanitización de entrada y helperText de errores
* --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect } from "react";
import { NumericFormat } from 'react-number-format';

import { Modal, Box, Typography } from "@mui/material";
import { NumberFormat$ } from "../../settings/utils";
import { useVentas } from '../../context/Ventas/VentasContext'
import { useDoubleClickPrevention } from '../../hooks/useDoubleClickPrevention';

// Estilos para el modal con efecto suavizado de desplazamiento (misma arquitectura de ModalCancelacionApartados)
const modalStyle = {
    position: 'fixed',
    top: '16%',
    right: '9%',
    width: '80vw',
    height: '65vh',
    maxWidth: '80vw',
    maxHeight: '75vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(300%)',
    transition: 'transform 0.5s ease-in-out',
    overflowY: 'auto',
    overflowX: 'hidden',
    borderRadius: '9px',
};

// Patrón seguro de moneda/descuento: signo negativo opcional al inicio, dígitos, punto decimal único y hasta 2 decimales
const rxDescuentoValido = /^-?\d*\.?\d{0,2}$/;

// Sanitiza el valor eliminando cualquier carécter que no sea numérico, punto decimal o signo negativo (evita inyección de código)
const sanitizeDescuentoInput = (value) => {
    const raw = String(value ?? "").replace(/[^\d.-]/g, "");
    const hasSigno = raw.startsWith("-");
    const sinSigno = hasSigno ? raw.slice(1) : raw;
    const [entera, ...decimal] = sinSigno.split(".");
    const enteraSegura = entera.replace(/[^0-9]/g, "").slice(0, 12);
    const decimalSegura = decimal.join("").replace(/[^0-9]/g, "").slice(0, 2);
    const numero = decimal.length > 0 ? `${enteraSegura}.${decimalSegura}` : enteraSegura;
    return hasSigno ? `-${numero}` : numero;
};

const getTotales = () => ({
    subTotal: 0,
    totalDescuento: 0,
    totalIVA: 0,
    total: 0,
    tipoPago: "EFECTIVO",
    montoTipoPago: 0,
    montoEfectivo: 0,
    montoDescuento: 0,
    totalCambioDevueltoACliente: 0,
    totalEnLetra: "",
    serialTarjetaDebitoCreditoChequeVale: "",
});

function NumericFormatCustom(props) {
    const { inputRef, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value, // Devuelve solo el valor numérico sin formato.
                    },
                });
            }}
            thousandSeparator // Añade separador de miles
            decimalSeparator="." // Define el separador decimal como un punto
            prefix="$" // Prefijo de moneda, opcional
            decimalScale={2} // Número de decimales
            allowNegative // Permite valores negativos para subir el costo
            isAllowed={(values) => {
                const { floatValue, value } = values;
                if (value === "" || value === undefined) return true;
                if (!rxDescuentoValido.test(value)) return false;
                if (floatValue === undefined) return true;
                return Math.abs(floatValue) <= 99999999999.99; // límite monetario seguro
            }}
        />
    );
}

export default function ModalCambioCostoArticulo(props) {
    const { openModalCambioCosto, costoArticulo, descuentoArticulo, handleCloseModalCambioCosto, handleCambiarCosto } = props;

    const { MontoEnLetra, openModalCambioCostoApartado } = useVentas();

    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const [ajusteDescuento, setAjusteDescuento] = useState(0);

    const [montoEnLetraArticulo, setMontoEnLetraArticulo] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);

    const modalVisibleStyle = {
        transform: modalVisible ? 'translateX(0)' : 'translateX(300%)',
    };

    const costoActual = Number(costoArticulo) || 0;
    const descuentoActual = Number(descuentoArticulo) || 0;
    const ajuste = Number(ajusteDescuento) || 0;
    const nuevoCosto = costoActual - ajuste;
    const nuevoDescuento = descuentoActual + ajuste;

    const textoAjuste = String(ajusteDescuento ?? "").trim();
    const formatoValido = textoAjuste === "" || (rxDescuentoValido.test(textoAjuste) && textoAjuste !== "-" && textoAjuste !== "." && textoAjuste !== "-.");
    const costoValido = nuevoCosto > 0;
    const descuentoValido = nuevoDescuento >= 0;

    const errorAjuste = !formatoValido
        ? "Solo se permiten números con hasta 2 decimales"
        : !costoValido
            ? "El nuevo costo debe ser mayor a 0"
            : !descuentoValido
                ? "El nuevo descuento no puede ser negativo"
                : "";

    const ajusteValido = formatoValido && costoValido && descuentoValido;

    const handleChangeAjusteDescuento = (e) => {
        setAjusteDescuento(sanitizeDescuentoInput(e.target.value));
    };

    const handleKeyDownAjusteDescuento = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAplicarDescuentoArticuloApartado();
        }
    };

    const handleAplicarDescuentoArticuloApartado = () => {
        if (!ajusteValido) return;
        executeWithProtection(() => {
            handleCambiarCosto({ nuevoCosto, nuevoDescuento })
        });
    };

    // Efecto para manejar la apertura/cierre con retardo para permitir la animación
    useEffect(() => {
        if (openModalCambioCosto) {
            setAjusteDescuento(0);
            setInternalOpen(true);
            setTimeout(() => {
                setModalVisible(true);
            }, 50);
        } else {
            setModalVisible(false);
            setTimeout(() => {
                setInternalOpen(false);
            }, 500);
        }
    }, [openModalCambioCosto]);

    useEffect(() => {
        const fetchMontoEnLetra = async () => {
            const letra = await MontoEnLetra(costoArticulo);
            setMontoEnLetraArticulo(letra);
        };

        if (costoArticulo > 0) {
            fetchMontoEnLetra();
        }
    }, [costoArticulo]);

    return (
        <Modal
            open={internalOpen}
            onClose={handleCloseModalCambioCosto}
            aria-labelledby="menu-modal-title"
            aria-describedby="menu-modal-description"
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, ...modalVisibleStyle }}>
                <Fragment>
                    <div className="venta-detalles-container">
                        <div className="detalle-item">
                            <span className="detalle-text">Costo actual:</span> {NumberFormat$(costoArticulo)}
                        </div>
                        <div className="detalle-item">
                            <span className="detalle-text">Descuento actual:</span> {NumberFormat$(descuentoArticulo)}
                        </div>
                        <div className="detalle-item">
                            <span className="detalle-text">Nuevo costo:</span> {NumberFormat$(nuevoCosto)}
                        </div>
                        <div className="detalle-item">
                            <span className="detalle-text">Nuevo descuento:</span> {NumberFormat$(nuevoDescuento)}
                        </div>
                        <div>
                            <span className="detalle-text">Monto en letra:</span> {montoEnLetraArticulo}
                        </div>
                    </div>
                    <div className="containerCobroVentas">
                        <TextField
                            label="Descuento a aplicar (negativo sube el costo):"
                            value={ajusteDescuento}
                            onChange={handleChangeAjusteDescuento}
                            onKeyDown={handleKeyDownAjusteDescuento}
                            onFocus={(e) => e.target.select()}
                            autoComplete="off"
                            InputProps={{
                                inputComponent: NumericFormatCustom, // Asigna el componente NumericFormatCustom como input
                            }}
                            error={ajusteDescuento !== 0 && ajusteDescuento !== "" && !ajusteValido}
                            helperText={ajusteDescuento !== 0 && ajusteDescuento !== "" && !ajusteValido ? errorAjuste : " "}
                            type="text"
                            sx={{ margin: '.5rem .3rem', width: '260px' }}
                        />
                        <button className="cambioCostoApartadoButton" onClick={handleAplicarDescuentoArticuloApartado} disabled={isProcessing || !ajusteValido}>Aplicar descuento</button>
                    </div>
                </Fragment>
            </Box>
        </Modal>
    );
}
